import type { Material } from '#domain/Material.ts';
import { isTransportOrder, type OrderDetails, type OrderSummary, type OrderType } from '#domain/Order.ts';
import { getVehicleDisplayName } from '#domain/Vehicle.ts';
import { apiV2Root } from '#store/apiV2Root.ts';

import { mapOrderDetails, mapOrderSummaries } from './adapters/orderAdapter.ts';
import { mapVehicles } from './adapters/resourcesAdapter.ts';
import { type ApiWriteOrder, apiSchema } from './model/types.ts';
import { validateWarn } from './validateZodHelper.ts';

const url = '/orders';
const TAG = 'ORDERS';
const VEHICLES_TAG = 'VEHICLES';

export const orderApiSlice = apiV2Root.injectEndpoints({
    endpoints: build => ({
        getOrders: build.query<OrderSummary[], OrderType[] | undefined>({
            query: types => ({ url, params: types?.length ? { types: types.join(',') } : undefined }),
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapOrderSummaries(validateWarn(apiSchema.Order.array())(response, meta, arg)),
        }),
        getOrdersWithTruckNames: build.query<OrderSummary[], OrderType[] | undefined>({
            queryFn: async (types, _queryApi, _extraOptions, baseQuery) => {
                const ordersResult = await baseQuery({
                    url,
                    params: types?.length ? { types: types.join(',') } : undefined,
                });

                if (ordersResult.error) {
                    return { error: ordersResult.error };
                }

                const orders = mapOrderSummaries(
                    validateWarn(apiSchema.Order.array())(ordersResult.data, ordersResult.meta, undefined)
                );

                if (!orders.some(order => order.truck?.id)) {
                    return { data: orders };
                }

                const vehiclesResult = await baseQuery('/resources/vehicles');

                if (vehiclesResult.error) {
                    return { data: orders };
                }

                const vehicles = mapVehicles(
                    validateWarn(apiSchema.Vehicle.array())(vehiclesResult.data, vehiclesResult.meta, undefined)
                );

                return { data: addTruckNamesToOrders(orders, vehicles) };
            },
            providesTags: [TAG, VEHICLES_TAG],
        }),
        getOrder: build.query<OrderDetails, string>({
            query: id => `${url}/${id}`,
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapOrderDetails(validateWarn(apiSchema.Order)(response, meta, arg)),
        }),
        createOrder: build.mutation<OrderDetails, ApiWriteOrder>({
            query: order => ({
                url: url,
                method: 'POST',
                body: JSON.stringify(order),
            }),
            invalidatesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapOrderDetails(validateWarn(apiSchema.Order)(response, meta, arg)),
        }),
        updateOrder: build.mutation<OrderDetails, { id: string; order: ApiWriteOrder }>({
            query: ({ id, order }) => ({
                url: `${url}/${id}`,
                method: 'PUT',
                body: JSON.stringify(order),
            }),
            invalidatesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapOrderDetails(validateWarn(apiSchema.Order)(response, meta, arg)),
        }),
        deleteOrder: build.mutation<void, string>({
            query: id => ({
                url: `${url}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [TAG],
        }),
        getOrderMaterials: build.query<Material[], string>({
            query: id => `${url}/${id}`,
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                (() => {
                    const order = mapOrderDetails(validateWarn(apiSchema.Order)(response, meta, arg));
                    return isTransportOrder(order) ? (order.materials ?? []) : [];
                })(),
        }),
    }),
});

export const {
    useGetOrdersQuery,
    useGetOrdersWithTruckNamesQuery,
    useCreateOrderMutation,
    useUpdateOrderMutation,
    useDeleteOrderMutation,
    useGetOrderMaterialsQuery,
} = orderApiSlice;

export const addTruckNamesToOrders = (
    orders: OrderSummary[],
    vehicles: ReturnType<typeof mapVehicles>
): OrderSummary[] => {
    const truckById = new Map(vehicles.map(vehicle => [vehicle.id, vehicle]));

    return orders.map(order => {
        const truck = order.truck?.id ? truckById.get(order.truck.id) : undefined;

        return {
            ...order,
            truck: order.truck
                ? {
                      ...order.truck,
                      name: truck ? getVehicleDisplayName(truck) : undefined,
                      hasOrderCommunicationLicense: truck?.hasOrderCommunicationLicense,
                      hasPocketDriverDataSource: truck?.hasPocketDriverDataSource,
                  }
                : undefined,
        };
    });
};
